import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static values = {
    variant: String,
    data: Object
  }

  static targets = ["chart"]

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const data = this.dataValue
    if (!data) return

    if (this.variantValue === "stacked_bar") {
      this.renderStackedBar(data)
    } else if (this.variantValue === "line") {
      this.renderLine(data)
    } else if (this.variantValue === "funnel") {
      this.renderFunnel(data)
    }
  }

  switchToBar() {
    this.variantValue = "stacked_bar"
    this.updateToggleUI("bar")
    if (this.chart) this.chart.destroy()
    this.renderChart()
  }

  switchToLine() {
    this.variantValue = "line"
    this.updateToggleUI("line")
    if (this.chart) this.chart.destroy()
    this.renderChart()
  }

  updateToggleUI(active) {
    const barBtn = this.element.querySelector(".moet-chart-toggle-bar")
    const lineBtn = this.element.querySelector(".moet-chart-toggle-line")
    if (!barBtn || !lineBtn) return

    const activeClasses = "bg-white text-support-dark-700 shadow-sm font-medium"
    const inactiveClasses = "text-support-dark-500"

    if (active === "bar") {
      barBtn.className = `moet-chart-toggle-bar text-xs px-2 py-1 rounded-md ${activeClasses}`
      lineBtn.className = `moet-chart-toggle-line text-xs px-2 py-1 rounded-md ${inactiveClasses}`
    } else {
      barBtn.className = `moet-chart-toggle-bar text-xs px-2 py-1 rounded-md ${inactiveClasses}`
      lineBtn.className = `moet-chart-toggle-line text-xs px-2 py-1 rounded-md ${activeClasses}`
    }
  }

  renderStackedBar(data) {
    const colors = [
      "#734CE0", "#E08C4C", "#4CADE0", "#E04C7A",
      "#4CE0A8", "#A84CE0", "#E0C84C", "#4C7AE0",
      "#E04CA8", "#7AE04C"
    ]

    const options = {
      chart: {
        type: "bar",
        stacked: true,
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: data.series || [],
      colors: colors.slice(0, (data.series || []).length),
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          columnWidth: "60%"
        }
      },
      xaxis: {
        categories: data.weeks || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        itemMargin: { vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderLine(data) {
    const colors = [
      "#734CE0", "#E08C4C", "#4CADE0", "#E04C7A",
      "#4CE0A8", "#A84CE0", "#E0C84C", "#4C7AE0",
      "#E04CA8", "#7AE04C"
    ]

    const seriesCount = (data.series || []).length
    const dashPatterns = [0, 5, 8, 3, 10, 4, 6, 12, 7, 9]
    const markerShapes = ["circle", "square", "diamond", "triangle", "star",
                          "circle", "square", "diamond", "triangle", "star"]

    const options = {
      chart: {
        type: "line",
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: data.series || [],
      colors: colors.slice(0, seriesCount),
      stroke: {
        width: 3,
        curve: "smooth",
        dashArray: dashPatterns.slice(0, seriesCount)
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        shape: markerShapes.slice(0, seriesCount)
      },
      xaxis: {
        categories: data.weeks || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        markers: { shape: markerShapes.slice(0, seriesCount) },
        itemMargin: { vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderFunnel(data) {
    const options = {
      chart: {
        type: "bar",
        height: 200,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: [{
        name: "Claims",
        data: [
          data.engaged || 0,
          data.submitted || 0,
          data.completed || 0,
          data.approved || 0
        ]
      }],
      colors: ["#734CE0"],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%"
        }
      },
      xaxis: {
        categories: ["Engaged", "Submitted", "Completed", "Approved"],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      yaxis: {
        labels: { style: { fontSize: "12px", colors: "#374151" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      dataLabels: {
        enabled: true,
        formatter: (val) => val.toLocaleString(),
        style: { fontSize: "12px", colors: ["#fff"] }
      },
      tooltip: {
        y: { formatter: (val) => val.toLocaleString() }
      }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }
}
